import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  STAMP_DUTY_DISCLAIMER,
  firstHomeDutyBullet,
  formatAud,
  sourceBullet,
  transferDutyBreakdown,
  transferDutyHeadline,
} from "@aucash/rules/stamp-duty";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// stamp-duty — transfer (stamp) duty calculator, surfaced stateless-v1 (PLAN
// arch decision #1). One screen, two modes (a plain transfer-duty run and a
// first-home run) over the state-keyed transfer-duty rulesets; the result
// renders INLINE (no save-by-id, no auth gate, no persistence). Each compute is
// a stateless `calc.stampDuty.compute` query — input -> output. Copy comes
// verbatim from the rules layer's copy helpers so the wording can never drift
// from the API/SEO surfaces (validation §3: information, not advice; the
// first-home concession is the DIFFERENCE of two cited duty figures, never
// "you save $X").
//
// NCCP / NABERS posture (binding): stamp duty is a STATE TAX, but this is the
// archetypal mortgage-broker lead hook. There is NO credit/loan/broker/lender
// handoff anywhere on this personalised surface — the rules-layer
// `assertNoCreditHandoff` guard would throw for any such identifier, and we
// surface only the guarded duty output here. No conveyancing/affiliate handoff
// on the compute surface either.

type State = "NSW" | "VIC" | "QLD";
type Mode = "transfer" | "first-home";

const STATE_OPTIONS: ReadonlyArray<{ value: State; label: string }> = [
  { value: "NSW", label: "NSW" },
  { value: "VIC", label: "VIC" },
  { value: "QLD", label: "QLD" },
];

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "transfer", label: "Transfer duty" },
  { value: "first-home", label: "First home" },
];

const MODE_COPY: Record<Mode, { eyebrow: string; title: string; blurb: string }> = {
  transfer: {
    eyebrow: "State tax · transfer duty",
    title: "What transfer (stamp) duty applies?",
    blurb:
      "See the transfer (stamp) duty on a property purchase, calculated from your state revenue office's current bracket table — plus the foreign-purchaser surcharge if it applies.",
  },
  "first-home": {
    eyebrow: "State tax · first home",
    title: "How does the first-home concession change the duty?",
    blurb:
      "Compare the full transfer duty against the duty payable with your state's first-home exemption or concession. In QLD, a new home attracts an uncapped nil-duty concession from 1 May 2025.",
  },
};

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.stampDuty.compute.query>
>;

export default function StampDutyIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("transfer");
  const [state, setState] = useState<State>("NSW");
  const [price, setPrice] = useState("");
  const [foreign, setForeign] = useState(false);
  const [newBuild, setNewBuild] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const onStateChange = (next: State): void => {
    setState(next);
    setResult(null);
    setSubmitError(null);
  };

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.stampDuty.compute.query>[0]["input"];
  } => {
    const residency = foreign ? ("foreign" as const) : ("domestic" as const);
    if (mode === "first-home") {
      return {
        input: {
          mode: "first-home",
          state,
          dutiableValue: num(price),
          residency,
          buildType: newBuild ? "new" : "established",
        },
      };
    }
    return {
      input: {
        mode: "transfer",
        state,
        dutiableValue: num(price),
        residency,
      },
    };
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (price.trim() === "")
      next.price = "Enter the purchase price (dutiable value).";
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0 || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.stampDuty.compute
      .query(buildInput())
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = MODE_COPY[mode];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Stamp duty" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="What are you working out?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          <Field label="Which state is the property in?">
            <Segmented<State>
              options={STATE_OPTIONS}
              value={state}
              onChange={onStateChange}
              testIDPrefix="state"
            />
          </Field>

          <Field
            label="Purchase price (dutiable value)"
            help="The contract price or dutiable value of the property, in whole dollars."
            error={errors.price}
          >
            <MoneyInput
              value={price}
              onChange={setPrice}
              testID="input-price"
              accessibilityLabel="Purchase price (dutiable value)"
            />
          </Field>

          {mode === "first-home" ? (
            <Field label="Is this a new (or substantially renovated, never-occupied) home?">
              <Checkbox
                checked={newBuild}
                onChange={setNewBuild}
                label="Yes — this is a new build (material for the QLD uncapped new-home concession)."
              />
            </Field>
          ) : null}

          <Field label="Is the buyer a foreign person?">
            <Checkbox
              checked={foreign}
              onChange={setForeign}
              label="Yes — a foreign-purchaser surcharge applies to residential land."
            />
          </Field>

          <Button
            label={submitting ? "Calculating…" : "See the duty →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="stamp-duty-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer STAMP_DUTY_DISCLAIMER) sits at the TOP,
 * before any number (validation §3.2 placement rule). */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const { headline, lines } = renderFor(result);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{STAMP_DUTY_DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your figure · {result.state} {result.period}</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        {lines.map((line, i) => (
          <Text key={i} style={styles.bodyText}>
            {line}
          </Text>
        ))}
      </Card>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          {sourceBullet(result.state, result.period, result.sourcesVerifiedAt)}
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
        <Text style={styles.verified}>
          Rules last verified {result.sourcesVerifiedAt} · ruleset{" "}
          {result.ruleVersion}
        </Text>
      </Section>
    </View>
  );
}

/** Pick the rules-layer copy helpers for the result's mode. All wording comes
 * from the rules package — no inline figures, no advice framing, the first-home
 * concession framed as a difference of two cited duty figures. */
function renderFor(result: ComputeResult): {
  headline: string;
  lines: string[];
} {
  const out = result.output;
  if (result.mode === "transfer") {
    const o = out as Extract<typeof out, { totalDuty: number }>;
    return { headline: transferDutyHeadline(o), lines: transferDutyBreakdown(o) };
  }
  const o = out as Extract<typeof out, { concessionValue: number }>;
  const headline =
    o.concessionDuty === o.fullDuty
      ? `First-home transfer duty in ${o.state} on a ${formatAud(o.dutiableValue)} purchase: ${formatAud(o.concessionDuty)}`
      : `First-home transfer duty in ${o.state} on a ${formatAud(o.dutiableValue)} purchase: ${formatAud(o.concessionDuty)} (full duty ${formatAud(o.fullDuty)})`;
  return { headline, lines: [firstHomeDutyBullet(o)] };
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
  verified: { fontFamily: fonts.body, fontSize: 12, color: colors.muted },
});
